/**
 * Keeps high-resolution art at its logical size.
 *
 * SVGs rasterise above their authored size so line art stays sharp. How far
 * above is not `devicePixelRatio` alone: the 1280x720 stage is CSS-scaled by
 * Phaser's FIT mode to fill the window, so on an ordinary 1920px-wide desktop
 * at DPR 1 the art is already being shown at 1.5x. Measuring only the device
 * ratio there reports 1x and the art goes soft, which is exactly what it did.
 *
 * Placeholder textures are still generated at 1x, and large background layers
 * are deliberately loaded at 1x, so there is no single divisor. The loader
 * records the resolution it used per key and `artScale` reads it back.
 */
import type Phaser from 'phaser';
import { stageResolution } from './stageResolution';

/** Map of key -> the resolution it was rasterised at, published by the loader. */
export const ART_KEYS_REGISTRY = 'artKeys';

/** Cached result of artResolution, so every scene agrees on one number. */
const ART_RESOLUTION_REGISTRY = 'artResolution';

/**
 * Asset groups that load at 1x however sharp the screen is.
 *
 * Background layers are soft gradients about a megapixel each. Doubling one
 * buys no visible sharpness and costs roughly 22MB of GPU memory, and there
 * are thirty of them - the whole set at 2x came to 426MB, which is not a
 * number a school Chromebook survives.
 *
 * Deliberately a group and not a size threshold. Size is only a proxy for
 * "is this a gradient", and it misfires: the 800x240 logo is line art, which
 * is exactly the kind of asset that has to stay crisp.
 */
export const LOW_RES_GROUPS = ['backgrounds/'] as const;

/**
 * How many texture pixels one logical pixel of art is actually shown at.
 *
 * The same number the canvas itself is drawn at (hiDpiRenderer.ts): device
 * ratio times how far the stage is scaled to fill the window. Art rasterised
 * at exactly that size lands one texture pixel per screen pixel. Worked out
 * once and cached: textures are not re-rasterised when the window resizes, so
 * a single stable number keeps every scene's art consistent with the others.
 */
export function artResolution(scene: Phaser.Scene): number {
  const cached: unknown = scene.registry.get(ART_RESOLUTION_REGISTRY);
  if (typeof cached === 'number' && cached > 0) return cached;
  const resolution = stageResolution();
  scene.registry.set(ART_RESOLUTION_REGISTRY, resolution);
  return resolution;
}

/** The resolution a given asset should rasterise at. */
export function resolutionFor(key: string, resolution: number): number {
  return LOW_RES_GROUPS.some((group) => key.startsWith(group)) ? 1 : resolution;
}

/** Multiplier that renders `key` at its authored size, whatever it loaded as. */
export function artScale(scene: Phaser.Scene, key: string): number {
  const loaded: unknown = scene.registry.get(ART_KEYS_REGISTRY);
  if (!(loaded instanceof Map)) return 1;
  const resolution: unknown = loaded.get(key);
  return typeof resolution === 'number' && resolution > 0 ? 1 / resolution : 1;
}
